import React, { useState } from 'react';
import { ArrowLeft, CreditCard, CheckCircle, AlertCircle, Loader, Lock } from 'lucide-react';

const PaymentStep = ({ packageData, customerInfo, onSuccess, onBack }) => {
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const [cardData, setCardData] = useState({
    cardholderName: '',
    email: customerInfo.email || '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      const cleaned = value.replace(/\s/g, '');
      const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
      setCardData(prev => ({ ...prev, [name]: formatted }));
    } 
    // Limit CVV to 4 digits
    else if (name === 'cvv') {
      if (value.length <= 4 && /^\d*$/.test(value)) {
        setCardData(prev => ({ ...prev, [name]: value }));
      }
    }
    // Limit expiry month to 2 digits
    else if (name === 'expiryMonth') {
      if (value.length <= 2 && /^\d*$/.test(value)) {
        setCardData(prev => ({ ...prev, [name]: value }));
      }
    }
    // Limit expiry year to 2 digits
    else if (name === 'expiryYear') {
      if (value.length <= 2 && /^\d*$/.test(value)) {
        setCardData(prev => ({ ...prev, [name]: value }));
      }
    }
    else {
      setCardData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      // Validate card data
      if (!cardData.cardholderName || !cardData.cardNumber || !cardData.expiryMonth || !cardData.expiryYear || !cardData.cvv) {
        setError('Please fill in all required fields');
        setIsProcessing(false);
        return;
      }

      // Remove spaces from card number for validation
      const cleanCardNumber = cardData.cardNumber.replace(/\s/g, '');
      if (cleanCardNumber.length < 13 || cleanCardNumber.length > 19) {
        setError('Invalid card number');
        setIsProcessing(false);
        return;
      }

      // Validate expiry
      const month = parseInt(cardData.expiryMonth);
      if (month < 1 || month > 12) {
        setError('Invalid expiry month (01-12)');
        setIsProcessing(false);
        return;
      }

      // Call backend to process payment
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/create-card-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package: packageData,
          customer: customerInfo,
          paymentMethod: paymentMethod,
          cardData: {
            ...cardData,
            cardNumber: cleanCardNumber
          },
          amount: parseFloat(packageData.price.replace(/[$,]/g, ''))
        }),
      });

      const result = await response.json();
      
      if (response.ok && result.status === 'success') {
        onSuccess(result);
      } else {
        setError(result.message || 'Payment processing failed. Please try again.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment processing failed. Please check your card details and try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Method</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
          <div>
            <p className="text-red-800 font-semibold">Payment Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="mb-8">
        <p className="text-gray-600 mb-4">Select your preferred payment method:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setPaymentMethod('card')}
            className={`p-4 border-2 rounded-xl transition-all duration-300 ${
              paymentMethod === 'card'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                paymentMethod === 'card' ? 'border-blue-500' : 'border-gray-300'
              }`}>
                {paymentMethod === 'card' && (
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">Credit/Debit Card</p>
                <p className="text-sm text-gray-600">Pay with your card</p>
              </div>
              <CreditCard className="text-gray-600" size={24} />
            </div>
          </button>

          <button
            type="button"
            onClick={() => setPaymentMethod('paypal')}
            className={`p-4 border-2 rounded-xl transition-all duration-300 ${
              paymentMethod === 'paypal'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                paymentMethod === 'paypal' ? 'border-blue-500' : 'border-gray-300'
              }`}>
                {paymentMethod === 'paypal' && (
                  <div className="w-3 h-3 bg-blue-500 rounded-full" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-gray-900">PayPal</p>
                <p className="text-sm text-gray-600">Pay with PayPal account</p>
              </div>
              <img
                src="https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg"
                alt="PayPal"
                className="h-6"
              />
            </div>
          </button>
        </div>
      </div>

      {/* Card Payment Form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="cardholderName"
              value={cardData.cardholderName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
              placeholder="John Doe"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={cardData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
              placeholder="john@example.com"
            />
          </div>

          {/* Card Number */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Card Number *
            </label>
            <div className="relative">
              <input
                type="text"
                name="cardNumber"
                value={cardData.cardNumber}
                onChange={handleInputChange}
                required
                maxLength="19"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none pr-12"
                placeholder="1234 5678 9012 3456"
              />
              <CreditCard className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
            </div>
          </div>

          {/* Expiry and CVV */}
          <div className="grid grid-cols-2 gap-4">
            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Expiry (MM/YY) *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="expiryMonth"
                  value={cardData.expiryMonth}
                  onChange={handleInputChange}
                  required
                  maxLength="2"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
                  placeholder="MM"
                />
                <span className="flex items-center text-gray-400 font-bold">/</span>
                <input
                  type="text"
                  name="expiryYear"
                  value={cardData.expiryYear}
                  onChange={handleInputChange}
                  required
                  maxLength="2"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
                  placeholder="YY"
                />
              </div>
            </div>

            {/* CVV */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                CVV *
              </label>
              <input
                type="text"
                name="cvv"
                value={cardData.cvv}
                onChange={handleInputChange}
                required
                maxLength="4"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
                placeholder="123"
              />
            </div>
          </div>

          {/* Billing Address (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Billing Address (Optional)
            </label>
            <input
              type="text"
              name="billingAddress"
              value={cardData.billingAddress}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0 transition-all duration-300 outline-none"
              placeholder="123 Main St, City, State, ZIP"
            />
          </div>

          {/* Pay Now Button */}
          <button
            type="submit"
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isProcessing ? (
              <>
                <Loader className="animate-spin" size={24} />
                Processing...
              </>
            ) : (
              <>
                <Lock size={24} />
                Pay Now {packageData.price}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Security Notice */}
      <div className="mb-8 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
          <div>
            <p className="font-semibold text-gray-900">Secure Payment</p>
            <p>Your payment information is encrypted and secure. We never store your card details.</p>
          </div>
        </div>
      </div>

      {/* Back Button */}
      <button
        type="button"
        onClick={onBack}
        disabled={isProcessing}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeft size={20} />
        Back to Information
      </button>
    </div>
  );
};

export default PaymentStep;
